import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AskVisualization as AskVisualizationType } from "@/types/marketReport";
import { REPORT_COLORS } from "@/utils/distinctColors";
import { pivotAskChartSeries, seriesUnitKey } from "./askChartPivot";

/** Series colours in order; the first matches the original single-series chart. */
const SERIES_COLORS = [
  REPORT_COLORS.green,
  REPORT_COLORS.slate,
  REPORT_COLORS.orange,
  REPORT_COLORS.teal,
  REPORT_COLORS.taupe,
  REPORT_COLORS.charcoal,
  REPORT_COLORS.gray,
] as const;

function AskTable({ viz }: { viz: Extract<AskVisualizationType, { type: "table" }> }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {viz.title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {viz.columns.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {viz.rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AskChart({ viz }: { viz: Extract<AskVisualizationType, { type: "chart" }> }) {
  const { rows: chartData, series } = pivotAskChartSeries(viz.series);
  const isLine = viz.chart_type === "line";
  const ChartComponent = isLine ? LineChart : BarChart;

  const tooltipFormatter = (
    value: unknown,
    name: unknown,
    item: { dataKey?: unknown; payload?: Record<string, unknown> },
  ): [string, string] => {
    const unit =
      typeof item.dataKey === "string"
        ? item.payload?.[seriesUnitKey(item.dataKey)]
        : undefined;
    const display =
      value == null ? "" : unit ? `${String(value)} ${String(unit)}` : String(value);
    return [display, String(name ?? "")];
  };

  return (
    <Box sx={{ mt: 2, height: 280 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {viz.title}
      </Typography>
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={tooltipFormatter} />
          {series.length > 1 && <Legend verticalAlign="top" height={28} />}
          {series.map((s, index) => {
            const color = SERIES_COLORS[index % SERIES_COLORS.length];
            return isLine ? (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={color}
                fill={color}
                connectNulls
              />
            ) : (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={color} stroke={color} />
            );
          })}
        </ChartComponent>
      </ResponsiveContainer>
    </Box>
  );
}

export function AskVisualizationList({
  visualizations,
}: {
  visualizations: AskVisualizationType[];
}) {
  if (visualizations.length === 0) return null;

  return (
    <Box>
      {visualizations.map((viz, index) =>
        viz.type === "table" ? (
          <AskTable key={`table-${index}`} viz={viz} />
        ) : (
          <AskChart key={`chart-${index}`} viz={viz} />
        ),
      )}
    </Box>
  );
}
